import type { PendingDose, Checkup } from '@rondela/shared'
import { parseLocalDateTime } from '../../../shared/lib/date-utils'

// Time window in milliseconds - items within 2 hours form a batch
const BATCH_WINDOW_MS = 2 * 60 * 60 * 1000

/**
 * Unified timeline item - can be a dose or an appointment
 */
export type TimelineItem =
  | { type: 'dose'; data: PendingDose; time: Date }
  | { type: 'appointment'; data: Checkup; time: Date }

/**
 * A batch of timeline items within a 2-hour window
 */
export interface TimelineBatch {
  items: TimelineItem[]
  startTime: Date
  endTime: Date
  isExpanded: boolean
}

/**
 * Time of day period
 */
export type TimeOfDay = 'morning' | 'afternoon' | 'evening'

/**
 * Get the time-of-day icon for a given hour
 */
export function getTimeOfDayIcon(hour: number): string {
  if (hour < 12) return '☀️' // Morning: 00:00 - 11:59
  if (hour < 18) return '🌅' // Afternoon: 12:00 - 17:59
  return '🌙' // Evening: 18:00 - 23:59
}

/**
 * Get the time-of-day label for a given hour
 */
export function getTimeOfDayLabel(hour: number): string {
  if (hour < 12) return 'Mañana'
  if (hour < 18) return 'Tarde'
  return 'Noche'
}

/**
 * Format a time range for display (e.g., "08:00 - 09:30")
 */
export function formatBatchTimeRange(startTime: Date, endTime: Date): string {
  const formatTime = (d: Date) =>
    d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })

  if (startTime.getTime() === endTime.getTime()) {
    return formatTime(startTime)
  }

  return `${formatTime(startTime)} - ${formatTime(endTime)}`
}

/**
 * Create a unified timeline from doses and appointments
 */
export function createUnifiedTimeline(
  doses: PendingDose[],
  appointments: Checkup[]
): TimelineItem[] {
  const items: TimelineItem[] = []

  // Add doses
  for (const dose of doses) {
    items.push({
      type: 'dose',
      data: dose,
      time: parseLocalDateTime(dose.scheduledFor),
    })
  }

  // Add appointments with proximaCita (upcoming appointments)
  for (const appointment of appointments) {
    if (appointment.proximaCita) {
      // Combine date and time
      const dateStr = appointment.proximaCita
      const timeStr = appointment.hora || '09:00'
      const [hours, minutes] = timeStr.split(':').map(Number)
      const appointmentTime = new Date(dateStr)
      appointmentTime.setHours(hours!, minutes!, 0, 0)

      items.push({
        type: 'appointment',
        data: appointment,
        time: appointmentTime,
      })
    }
  }

  // Sort by time
  items.sort((a, b) => a.time.getTime() - b.time.getTime())

  return items
}

/**
 * Group timeline items into batches based on 2-hour proximity
 *
 * Rules:
 * 1. First item starts a batch
 * 2. Items within 2 hours of the FIRST item in the batch join that batch
 * 3. Items beyond 2 hours start a new batch
 */
export function groupIntoBatches(items: TimelineItem[]): TimelineBatch[] {
  if (items.length === 0) return []

  const batches: TimelineBatch[] = []
  let currentBatch: TimelineBatch | null = null

  for (const item of items) {
    if (!currentBatch) {
      // Start first batch
      currentBatch = {
        items: [item],
        startTime: item.time,
        endTime: item.time,
        isExpanded: false,
      }
    } else {
      // Check if item falls within 2 hours of batch start
      const msSinceBatchStart = item.time.getTime() - currentBatch.startTime.getTime()

      if (msSinceBatchStart <= BATCH_WINDOW_MS) {
        // Add to current batch
        currentBatch.items.push(item)
        currentBatch.endTime = item.time
      } else {
        // Save current batch and start new one
        batches.push(currentBatch)
        currentBatch = {
          items: [item],
          startTime: item.time,
          endTime: item.time,
          isExpanded: false,
        }
      }
    }
  }

  // Don't forget the last batch
  if (currentBatch) {
    batches.push(currentBatch)
  }

  return batches
}

/**
 * Split batches into immediate (urgent/overdue or within next 2 hours) and later
 */
export function splitImmediateAndLater(
  batches: TimelineBatch[]
): { immediateBatches: TimelineBatch[]; laterItems: TimelineItem[] } {
  const now = new Date()
  const twoHoursFromNow = new Date(now.getTime() + BATCH_WINDOW_MS)

  const immediateBatches: TimelineBatch[] = []
  const laterItems: TimelineItem[] = []

  for (const batch of batches) {
    // A batch is immediate if:
    // - Any item is overdue (past now)
    // - OR the batch starts within 2 hours from now
    const hasOverdueItem = batch.items.some((item) => item.time < now)
    const startsWithin2Hours = batch.startTime <= twoHoursFromNow

    if (hasOverdueItem || startsWithin2Hours) {
      immediateBatches.push(batch)
    } else {
      // Add all items from this batch to laterItems
      laterItems.push(...batch.items)
    }
  }

  return { immediateBatches, laterItems }
}

/**
 * Format times for the collapsed "later" chip
 * e.g., "+2 dosis posteriores (14:00, 20:00)"
 */
export function formatLaterItemsTimes(items: TimelineItem[]): string {
  const times = items
    .slice(0, 3) // Show max 3 times
    .map((item) =>
      item.time.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })
    )

  if (items.length > 3) {
    return `${times.join(', ')}...`
  }

  return times.join(', ')
}
